import React, { useContext, useEffect, useState } from 'react';
import { Grid, Box, Button, Paper, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import { AddBox, Delete } from '@material-ui/icons';
import { ScrollContainer } from 'react-indiana-drag-scroll';

import { WebContext } from 'src/scenes/web-context';
import { TitleContainer, ContentContainer, ActionsBox, DialogWrap, ChartWrapper, AlertWrapper, AccessControl } from 'src/components';
import { AnalyticCreateDialog, AnalyticDeleteDialog } from 'src/scenes/Project/scenes/Analytics/components';
import { useMonitoringPlanApi } from 'src/services';
import { buildChartData, chartDefaultOptions, htmlLegendPlugin } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { deepObjClone, removeUndefinedPropsFromObject as rupfo } from 'src/utils/util';
import { useFetchStatus } from 'src/hooks';


const useStyles = makeStyles(theme => ({
  backArrow: {
    color: theme.palette.common.gray700,
    padding: 0,
    marginRight: theme.spacing(1),
  },
  paper: {
    height: '100%',
    '&:hover': {
      '& $actionIcons': {
        visibility: 'visible',
        animationName: '$fadeIn',
        animation: 'linear',
        animationDuration: '300ms',
      },
    },
    padding: theme.spacing(2),
  },
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  actionIcons: {
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.gray600,
    cursor: 'pointer',
    visibility: 'hidden',
    margin: theme.spacing(0.25),
    '&:hover': {
      backgroundColor: theme.palette.common.gray400,
    },
  },
  map: {
    borderRadius: theme.shape.borderRadius,
  },
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: theme.spacing(1),
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '6px',
      backgroundColor: theme.palette.common.gray300,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '6px',
      backgroundColor: '#555',
    },
  },
}));

const AnalyticsContainer = props => {
  const classes = useStyles();

  const match = useRouteMatch();
  const monitoringPlanApi = useMonitoringPlanApi();

  const { fetchStatus, setFetchStatus, fetchStatusEnum, errorMessage, setErrorMessage } = useFetchStatus({});
  const { LOADING, SUCCESS, ERROR } = fetchStatusEnum;

  const { monitoringPlanId } = match.params;

  const webContext = useContext(WebContext);
  const { currentUser: { roleId } } = webContext;
  const isAdmin = roleId === 'admin';

  const [ analyticsCharts, setAnalyticsCharts ] = useState([]);
  const [ chartTypeOptions, setChartTypeOptions ] = useState([ { label: '', value: '' } ]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchStatus(LOADING);

        const [ analyticsData, chartTypes ] = await Promise.all([
          monitoringPlanApi.getAnalyticsData(rupfo({ monitoringPlanId })),
          monitoringPlanApi.getChartTypes(monitoringPlanId),
        ]);

        const chartTypeOptions = chartTypes.map(cto => ({ label: cto.name, value: cto.id }));

        const analyticsCharts = analyticsData.map(analytic => {
          const { name: title } = analytic;
          const { separatedKey, separatedInfo, isStacked, chartJsType = 'bar', sort, isLogarithmic,
            valueToChart, valuesToChart, customXLabels } = analytic.config;
          const fixedValToChart = valueToChart ? [ valueToChart ] : valuesToChart;
          const chart = buildChartData({
            valuesToChart: fixedValToChart,
            separatedKey,
            separatedInfo,
            customXLabels,
            dataToChart: analytic.data,
            hasSomeSeparatedData: chartTypeOptions.map(cto => cto.value).includes(separatedKey) || fixedValToChart.length > 1,
            sortType: sort,
          });
          const chartOptions = deepObjClone(chartDefaultOptions);
          chartOptions.scales.x.stacked = isStacked;
          chartOptions.scales.y = { stacked: isStacked, ...(isLogarithmic ? { type: 'logarithmic' } : {}) };
          chartOptions.plugins.htmlLegend = { containerId: analytic.id };

          return { id: analytic.id, title, chart, options: chartOptions, chartJsType };
        });

        setAnalyticsCharts(analyticsCharts);
        setChartTypeOptions(chartTypeOptions);
        setFetchStatus(SUCCESS);

      } catch (e) {
        console.error(e);
        setErrorMessage(e.serverMessage || e.message);
        setFetchStatus(ERROR);
      }
    };

    if (webContext.permissions) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [ monitoringPlanId, webContext.permissions ]);

  const [ dialog, setDialog ] = useState({ isOpen: false, data: {}, type: '' });

  const closeDialog = () => setDialog({ isOpen: false, type: '' });

  const openCreateAnalyticDialog = () => setDialog({ isOpen: true, type: 'create' });
  const openDeleteAnalyticDialog = analytic => setDialog({ isOpen: true, type: 'delete', data: analytic });

  const deleteAnalytic = async analyticId => {
    await monitoringPlanApi.deleteAnalytic(analyticId);
    setAnalyticsCharts(prevA => prevA.filter(analytic => analytic.id !== analyticId));
  };

  const getDialogType = () => {
    switch (dialog.type) {
      case 'create' : {
        return <AnalyticCreateDialog actions={{ saveAnalytic, closeDialog }} options={{ chartTypeOptions }} />;
      }
      case 'delete':
        return <AnalyticDeleteDialog actions={{ deleteAnalytic, closeDialog }} analytic={dialog.data}/>;
      default:
        break;
    }
  };

  const saveAnalytic = async ({ monitoringPlanId, chartData, chartPreview }) => {
    const chart = await monitoringPlanApi.saveAnalytic({ monitoringPlanId, chartData });
    const options = deepObjClone(chartPreview.options);
    options.plugins.htmlLegend = { containerId: chart.id };
    setAnalyticsCharts([
      ...analyticsCharts,
      {
        id: chart.id,
        title: chartPreview.title,
        chart: chartPreview.chart,
        options,
        chartJsType: chartPreview.chartJsType,
      },
    ]);
  };

  return (
    <>
      <Grid container>
        <TitleContainer
          maxWidth="xl"
          project={props.project}
          monitoringPlanName={props.name}
          breadcrumbs={[ 'projects', 'project', 'sites', 'site', 'monitoring-plans', { name: `Analíticas de ${props.name}` } ]}
        >
          Analíticas
        </TitleContainer>
        <ContentContainer maxWidth="xl">
          {isAdmin &&
          <ActionsBox mt={2} mb={1} display="flex" alignItems="flex-end">
            <AccessControl action='monitoring-plan:create-chart'>
              <Box display="flex" height="30px">
                <Button
                  disabled={fetchStatus === LOADING}
                  size="small"
                  color="primary"
                  startIcon={<AddBox />}
                  variant="contained"
                  onClick={openCreateAnalyticDialog}
                >
                  Agregar analítica
                </Button>
              </Box>
            </AccessControl>
          </ActionsBox>
          }
          <Grid container spacing={1}>
            {fetchStatus === ERROR && <Box py={1}><AlertWrapper severity="error">{errorMessage}</AlertWrapper></Box>}

            {analyticsCharts.map(ac =>
              <Grid key={`ac-${ac.id}`} item xs={6}>
                <Paper className={classes.paper}>
                  <Box p={2} display="flex" flexDirection="column" height="100%">
                    {isAdmin && <AccessControl action='monitoring-plan:delete-chart'>
                      <Box display="flex" alignSelf="end">
                        <Delete fontSize="small" className={classes.actionIcons} onClick={() => openDeleteAnalyticDialog(ac)} />
                      </Box>
                    </AccessControl>}
                    <Box textAlign="center" display="flex" justifyContent="center">
                      <Typography variant="h6">{ac.title}</Typography>
                    </Box>
                    <br />
                    <Box display={ac.chart.datasets.length === 1 ? 'flex' : 'block' } height="100%" alignItems="flex-end">
                      <ScrollContainer hideScrollbars={false} className={classes.scrollbar}>
                        <Box id={ac.id} maxHeight={104} />
                      </ScrollContainer>
                      <ChartWrapper type={ac.chartJsType} options={ac.options} data={ac.chart} plugins={[ htmlLegendPlugin ]} />
                    </Box>
                  </Box>
                </Paper>
              </Grid>,
            )}
          </Grid>
        </ContentContainer>
      </Grid>
      <DialogWrap maxWidth={dialog.type === 'delete' ? 'sm' : 'lg'} fullWidth onClose={closeDialog} open={dialog.isOpen}>
        {getDialogType()}
      </DialogWrap>
    </>
  );
};

AnalyticsContainer.propTypes = {
  name: PropTypes.string,
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export {
  AnalyticsContainer,
};
